import { defineModuleEvidenceContract } from './moduleEvidenceContract';

// K3 (2026-08-18), amended per Codex exact-head review (#4602 comment
// 5324455668): workforce-reduction's six manifest-required evidence keys are
// NOT backed by the module's AskHelixMemoryArtifact evidence pack. The
// runtime builds the governed state — and the six class-specific evidence
// refs — from the WorkforceReductionRun / program / portfolio data in
// buildGovernedWorkforceDecisionStateFromRun
// (workforce-reduction.data-source.ts:455-526); the pack is loaded only
// afterwards as OPTIONAL formula/scenario augmentation
// (resolveState returns `formulaBinding && selectedCase ? {...state,
// formulaRuntimeEvidence} : state`). Registering the six keys against the
// pack asserted the wrong provenance and let one matching pack row mark all
// six cells PRESENT at the measurement seam, so the registration was
// replaced by this typed non-artifact classification: each key names its
// real source authority, machine-readably, and the registration census
// (evidence-contract-registration.conformance.spec.ts) proves per-key
// completeness as manifest required = artifact-backed ∪ non-artifact.
export const WORKFORCE_REDUCTION_EVIDENCE_KEYS = {
  orgStateAsIs: 'org_state_as_is',
  positionCostRoster: 'position_cost_roster',
  peopleTenure: 'people_tenure',
  severancePolicy: 'severance_policy',
  initiativeCapacityBinding: 'initiative_capacity_binding',
  workforceLegalGate: 'workforce_legal_gate',
} as const;

const RUN_STATE_AUTHORITY =
  'buildGovernedWorkforceDecisionStateFromRun (workforce-reduction.data-source.ts) — ' +
  'the governed WorkforceReductionRun/result data, not an AskHelixMemoryArtifact';

export const WORKFORCE_REDUCTION_MODULE_EVIDENCE_CONTRACT = defineModuleEvidenceContract(
  'workforce-reduction',
  {},
  {
    evidenceKeys: [],
    sourceClasses: [],
    contextKinds: [],
    schemaVersions: [],
    stateKeys: [],
    bindingPlaceholders: [],
  },
  [
    {
      evidenceKey: WORKFORCE_REDUCTION_EVIDENCE_KEYS.orgStateAsIs,
      sourceAuthority:
        'run.asIsOrgStateId — the pinned AS-IS org state input to the governed run, ' +
        `surfaced via ${RUN_STATE_AUTHORITY}`,
      reason:
        'Resolves from the governed run record, not an artifact substrate; the evidence ' +
        'pack is optional augmentation loaded after the state exists.',
    },
    {
      evidenceKey: WORKFORCE_REDUCTION_EVIDENCE_KEYS.positionCostRoster,
      sourceAuthority:
        'result.inputSnapshotId — the position cost roster embedded in the governed run ' +
        `input, surfaced via ${RUN_STATE_AUTHORITY}`,
      reason:
        'Resolves from the governed run input snapshot, not an artifact substrate; the ' +
        'evidence pack is optional augmentation loaded after the state exists.',
    },
    {
      evidenceKey: WORKFORCE_REDUCTION_EVIDENCE_KEYS.peopleTenure,
      sourceAuthority:
        'result.inputSnapshotId — the tenure evidence embedded in the governed run input, ' +
        `surfaced via ${RUN_STATE_AUTHORITY}`,
      reason:
        'Resolves from the governed run input snapshot, not an artifact substrate; the ' +
        'evidence pack is optional augmentation loaded after the state exists.',
    },
    {
      evidenceKey: WORKFORCE_REDUCTION_EVIDENCE_KEYS.severancePolicy,
      sourceAuthority:
        'legalGate.sourceId — the approved severance policy provenance from the workforce ' +
        `compliance gate, surfaced via ${RUN_STATE_AUTHORITY}`,
      reason:
        'Resolves from the run legal-gate record, not an artifact substrate; the ' +
        'evidence pack is optional augmentation loaded after the state exists.',
    },
    {
      evidenceKey: WORKFORCE_REDUCTION_EVIDENCE_KEYS.initiativeCapacityBinding,
      sourceAuthority:
        'result.portfolioSnapshot.strategySnapshotId — the strategy portfolio snapshot the ' +
        `run binds capacity against, surfaced via ${RUN_STATE_AUTHORITY}`,
      reason:
        'Resolves from the governed run portfolio binding, not an artifact substrate; the ' +
        'evidence pack is optional augmentation loaded after the state exists.',
    },
    {
      evidenceKey: WORKFORCE_REDUCTION_EVIDENCE_KEYS.workforceLegalGate,
      sourceAuthority:
        'legalGate.evidenceRefId — the server-owned workforce legal/compliance gate, ' +
        `surfaced via ${RUN_STATE_AUTHORITY}`,
      reason:
        'Resolves from the run legal-gate record, not an artifact substrate; the ' +
        'evidence pack is optional augmentation loaded after the state exists.',
    },
  ],
);

export type WorkforceReductionModuleEvidenceContract =
  typeof WORKFORCE_REDUCTION_MODULE_EVIDENCE_CONTRACT;
